import type { ActionItem } from "@elation/api-client/src/stitch-web/models/actionItem";
import { ActionItemCategory } from "@elation/api-client/src/stitch-web/models/actionItemCategory";
import { ActionItemStatus } from "@elation/api-client/src/stitch-web/models/actionItemStatus";

import type { GeneratedArtifactViewState } from "@src/shared/domain/generated-artifact";
import { resolveGeneratedArtifactViewState } from "@src/shared/domain/generated-artifact";
import { parseNullableDate } from "@src/shared/utils/dates";

export type ActionItemSection =
  | "Prescriptions"
  | "Orders"
  | "Referrals"
  | "Miscellaneous"
  | "Assessments"
  | "Patient Education";

export const ACTION_ITEM_SECTIONS = [
  "Prescriptions",
  "Orders",
  "Referrals",
  "Assessments",
  "Patient Education",
  "Miscellaneous",
] as const satisfies readonly ActionItemSection[];

type MaybeActionItem = ActionItem | null | undefined;

export type ActionItemListArtifact = {
  items: ActionItem[];
};

const CATEGORY_LABELS: Record<ActionItem["category"], string> = {
  [ActionItemCategory.Medication_Start]: "Start medication",
  [ActionItemCategory.Medication_Discontinuation]: "Discontinue medication",
  [ActionItemCategory.Medication_Refill]: "Refill medication",
  [ActionItemCategory.Medication_Details_Change]: "Change medication details",
  [ActionItemCategory.Medication_Continue]: "Continue medication",
  [ActionItemCategory.Lab_Order]: "Lab order",
  [ActionItemCategory.Imaging_Order]: "Imaging order",
  [ActionItemCategory.Medical_Referral]: "Medical referral",
  [ActionItemCategory.Other_Action]: "Other action",
  [ActionItemCategory.Document_Review]: "Document review",
  [ActionItemCategory.Advise_Patient]: "Advise patient",
  [ActionItemCategory.Take_Note]: "Take note",
  [ActionItemCategory.Physical_Exam]: "Physical exam",
  [ActionItemCategory.Symptom_Evaluation]: "Symptom evaluation",
};

const CATEGORY_SECTIONS: Record<ActionItem["category"], ActionItemSection> = {
  [ActionItemCategory.Medication_Start]: "Prescriptions",
  [ActionItemCategory.Medication_Discontinuation]: "Prescriptions",
  [ActionItemCategory.Medication_Refill]: "Prescriptions",
  [ActionItemCategory.Medication_Details_Change]: "Prescriptions",
  [ActionItemCategory.Medication_Continue]: "Prescriptions",
  [ActionItemCategory.Lab_Order]: "Orders",
  [ActionItemCategory.Imaging_Order]: "Orders",
  [ActionItemCategory.Medical_Referral]: "Referrals",
  [ActionItemCategory.Other_Action]: "Miscellaneous",
  [ActionItemCategory.Document_Review]: "Assessments",
  [ActionItemCategory.Advise_Patient]: "Patient Education",
  [ActionItemCategory.Take_Note]: "Miscellaneous",
  [ActionItemCategory.Physical_Exam]: "Assessments",
  [ActionItemCategory.Symptom_Evaluation]: "Assessments",
};

export const ActionItemEntity = {
  isCompleted(item: MaybeActionItem): boolean {
    if (!item) return false;
    return item.status === ActionItemStatus.completed;
  },

  canComplete(item: MaybeActionItem): boolean {
    if (!item) return false;
    return item.status === ActionItemStatus.pending;
  },

  canReopen(item: MaybeActionItem): boolean {
    if (!item) return false;
    return item.status === ActionItemStatus.completed;
  },

  nextStatus(item: MaybeActionItem): ActionItem["status"] | null {
    if (!item) return null;
    return ActionItemEntity.isCompleted(item)
      ? ActionItemStatus.pending
      : ActionItemStatus.completed;
  },

  categoryLabel(
    item: Pick<ActionItem, "category"> | null | undefined,
  ): string | null {
    if (!item) return null;
    return CATEGORY_LABELS[item.category];
  },

  section(
    item: Pick<ActionItem, "category"> | null | undefined,
  ): ActionItemSection | null {
    if (!item) return null;
    return CATEGORY_SECTIONS[item.category];
  },

  completedAt(item: MaybeActionItem): Date | null {
    return parseNullableDate(item?.completedAt);
  },

  updatedAt(item: MaybeActionItem): Date | null {
    return parseNullableDate(item?.updatedAt);
  },

  compareByUpdatedAtDesc(left: ActionItem, right: ActionItem): number {
    const leftDate = ActionItemEntity.updatedAt(left);
    const rightDate = ActionItemEntity.updatedAt(right);

    if (leftDate && rightDate) return rightDate.getTime() - leftDate.getTime();
    if (leftDate) return -1;
    if (rightDate) return 1;
    return left.id - right.id;
  },

  /**
   * Stable checklist order. Prefer this over `compareByUpdatedAtDesc` in
   * interactive lists — toggling status bumps `updatedAt` and would otherwise
   * make the row jump.
   */
  compareByCreatedAtAsc(left: ActionItem, right: ActionItem): number {
    const leftDate = parseNullableDate(left.createdAt);
    const rightDate = parseNullableDate(right.createdAt);

    if (leftDate && rightDate) {
      const delta = leftDate.getTime() - rightDate.getTime();
      if (delta !== 0) return delta;
    } else if (leftDate) {
      return -1;
    } else if (rightDate) {
      return 1;
    }
    return left.id - right.id;
  },

  groupBySection(
    items: readonly ActionItem[],
  ): Record<ActionItemSection, ActionItem[]> {
    const initialGroups: Record<ActionItemSection, ActionItem[]> = {
      Prescriptions: [],
      Orders: [],
      Referrals: [],
      Miscellaneous: [],
      Assessments: [],
      "Patient Education": [],
    };

    return items.reduce<Record<ActionItemSection, ActionItem[]>>(
      (groups, item) => {
        const section = ActionItemEntity.section(item);
        if (section) groups[section].push(item);
        return groups;
      },
      initialGroups,
    );
  },

  /**
   * V1-equivalent panel state from list query lifecycle only — no durable
   * generation-status field on action items.
   */
  resolveViewState(input: {
    itemCount: number;
    isInitialLoading: boolean;
    isFetchError: boolean;
  }): GeneratedArtifactViewState {
    return resolveGeneratedArtifactViewState(input);
  },
} as const;
